import { useState, useContext, useEffect } from "react";
import { NavBarContext } from '../../App';
import { Link, useNavigate, useLocation } from "react-router-dom";
import { useSelector } from 'react-redux';
import useUnreadMessagesCount from '../../hooks/messages/useUnreadMessagesCount';
import useBookingNotifications from '../../hooks/useBookingNotifications';
import { useHasStudios } from '../../hooks/useHasStudios';

import classes from "./NavBarPhone.module.css";

// import menuIcon from "../../public/icons/phoneNavBar/MenuIcon.svg";

const NavBarPhone = ({ 
    fromServicesStudios, 
    fromMessages, 
    fromProfile, 
    fromSearch, 
    fromBookings, 
    fromFavourite, 
    fromCalendar,
    searchResults,
    isLoading, 
    fromHomePage=false,
    error,
    fromSignIn=false,
    ...otherProps
}) => {
  const navigate = useNavigate();
  const location = useLocation();
                      
  const { 
      activeView, 
      activeButton, setActiveButton,
      setActiveView
     } = useContext(NavBarContext);
  const { isAuthenticated, user } = useSelector((state) => state.auth);
  const { hasStudios } = useHasStudios(user?.id);
  const [showProfileMenu, setShowProfileMenu] = useState(false);
  const [horizontalOffset, setHorizontalOffset] = useState(0);
  const unreadCount = useUnreadMessagesCount();
  
  // Only fetch booking notifications for hosts (creator mode), NOT in explorer mode
  // Guests (explorer mode) don't need booking notifications - they're waiting for host to respond
  const isHost = hasStudios || activeView === 'creator';
  // Always call hook unconditionally, but only use result in creator mode
  const bookingNotificationsRaw = useBookingNotifications(activeView === 'creator' ? activeView : null);
  const bookingNotifications = (isHost && activeView === 'creator') ? bookingNotificationsRaw : 0;

  useEffect(() => {
    if (fromHomePage === true) {
      setActiveButton("");
    }
  }, [fromHomePage, setActiveButton]);

  useEffect(() => {
    if (fromSignIn === true) {
      setActiveButton("signIn");
    }
  }, [fromSignIn, setActiveButton]);

  useEffect(() => {
    if (fromSearch === true) {
      setActiveButton("search");
    }
  }, [fromSearch, setActiveButton]);

  useEffect(() => {
    if (fromProfile === true) {
      setActiveButton("profile");
    }
  }, [fromProfile, setActiveButton]);

  useEffect(() => {
    if (fromMessages === true) {
      setActiveButton("messages");
    }
  }, [fromMessages, setActiveButton]);

  useEffect(() => {
    if (fromServicesStudios === true) {
      setActiveButton("servicesStudios");
    }
  }, [fromServicesStudios, setActiveButton]);

  useEffect(() => {
    if (fromFavourite === true) {
      setActiveButton("favourite");
    }
  }, [fromFavourite, setActiveButton]);

  useEffect(() => {
    if (fromBookings === true) {
      setActiveButton("bookings");
    }
  }, [fromBookings, setActiveButton]);

  useEffect(() => {
    if (fromCalendar === true) {
      setActiveButton("calendar");
    }
  }, [fromCalendar, setActiveButton]);

  useEffect(() => {
    const syncHorizontalOffset = () => {
      setHorizontalOffset(window.scrollX || window.pageXOffset || 0);
    };

    syncHorizontalOffset();
    window.addEventListener("scroll", syncHorizontalOffset, { passive: true });
    window.addEventListener("resize", syncHorizontalOffset);

    return () => {
      window.removeEventListener("scroll", syncHorizontalOffset);
      window.removeEventListener("resize", syncHorizontalOffset);
    };
  }, []);

  // Auto-detect creator vs explorer mode based on current route
  useEffect(() => {
    if (
        location.pathname === '/' 
        || location.pathname === '/bookings'
      ) {
      setActiveView('explorer');
    } else if (
        location.pathname === '/services-studios' 
        || location.pathname.startsWith('/services-studios/')
        || location.pathname === '/creator'
        || location.pathname.startsWith('/creator/')
        || location.pathname === '/calendar'
        || location.pathname === '/studio-erstellen'
        || location.pathname.startsWith('/studio-erstellen/')
      ) {
      setActiveView('creator');
    }
    // Note: /messages routes no longer automatically switch to creator mode
    // This preserves the user's current view (explorer or creator) when navigating to messages
  }, [location.pathname, setActiveView]);

  const handleButtonClick = (buttonName) => {
    if (buttonName !== activeButton) {
      setActiveButton(buttonName);
    }
  };

  const handleProfileClick = () => {
    setShowProfileMenu(true);
    handleButtonClick("profile");
  };

  const handleSignInClick = () => {
    navigate('/anmelden');
    handleButtonClick("signIn");
  };

  const fixedHeaderStyle = {
    transform: `translateX(-${horizontalOffset}px)`,
    minWidth: "335px",
    width: "100%"
  };

  return (
    <>
    {!isAuthenticated ? (
      <header 
        className={classes.header} 
        style={{
          justifyContent: isAuthenticated ? 'space-between' : 'center', 
          borderTop: fromSignIn ? '0.5px solid var(--dark-grey-4)' : 'none'
        }}
      >
        <Link 
          className={classes.authenticatedButton} 
          style={{
            paddingRight: '60px',
            alignItems: 'flex-end',	
          }}
          to={activeButton !== "search" ? "/search" : "#"} onClick={(e) => {
          if (activeButton === "search") {
            e.preventDefault();
          }
        }}>
          <div
            className={classes.navigationButtons}
            onClick={() => handleButtonClick("search")}
          >
            <div className={`${classes.navigationButtonsInner} ${activeButton === "search" ? classes.activeButton : ''}`}>
              {activeButton === "search" ? (
                <svg xmlns="http://www.w3.org/2000/svg" width="22" height="22" viewBox="0 0 22 22" fill="none">
                  <path d="M14.8794 14.0012C16.1584 12.5236 16.9321 10.5964 16.9321 8.49144C16.9321 3.83549 13.1474 0.0507812 8.49144 0.0507812C3.83549 0.0507812 0.0507812 3.83549 0.0507812 8.49144C0.0507812 13.1474 3.83549 16.9321 8.49144 16.9321C10.5964 16.9321 12.5236 16.1584 14.0012 14.8794L20.783 21.4312L21.6438 20.5426L14.876 14.0012H14.8794ZM8.49144 15.6949C4.51855 15.6949 1.28447 12.4608 1.28447 8.48796C1.28447 4.51506 4.51855 1.28795 8.49144 1.28795C12.4643 1.28795 15.6984 4.52203 15.6984 8.49492C15.6984 12.4678 12.4643 15.7019 8.49144 15.7019V15.6949Z" fill="black" stroke="black" strokeWidth="0.1"/>
                </svg>
              ) : (
                <svg xmlns="http://www.w3.org/2000/svg" width="22" height="22" viewBox="0 0 22 22" fill="none">
                <path d="M14.8287 13.9504C16.1076 12.4728 16.8813 10.5456 16.8813 8.44066C16.8813 3.7847 13.0966 0 8.44066 0C3.7847 0 0 3.7847 0 8.44066C0 13.0966 3.7847 16.8813 8.44066 16.8813C10.5456 16.8813 12.4728 16.1076 13.9504 14.8287L20.7322 21.3804L21.593 20.4918L14.8252 13.9504H14.8287ZM8.44066 15.6441C4.46776 15.6441 1.23369 12.4101 1.23369 8.43717C1.23369 4.46428 4.46776 1.23717 8.44066 1.23717C12.4136 1.23717 15.6476 4.47125 15.6476 8.44414C15.6476 12.417 12.4136 15.6511 8.44066 15.6511V15.6441Z" fill="#77808A"/>
                </svg>
              )}
            </div>
            <span className={activeButton === "search" ? classes.activeText : classes.nonActiveText}>
              Suchen
            </span>
          </div>
        </Link>
        <div
          style={{
            paddingLeft: '60px',
            alignItems: 'flex-start',
          }}
          className={`${classes.navigationButtons} ${classes.authenticatedButton} ${activeButton === "signIn" ? classes.activeButton : ''}`}
          onClick={handleSignInClick}
        >
          <div className={`${classes.navigationButtonsInner} ${activeButton === "signIn" ? classes.activeButton : ''}`}>
             {activeButton === "signIn" ? (
                <svg xmlns="http://www.w3.org/2000/svg" width="24" height="21" viewBox="0 0 24 21" fill="none">
                <path d="M22.9855 20.64H0.0507812V20.1208C0.0507812 15.0118 4.2049 10.8577 9.3139 10.8577H13.7224C18.8314 10.8577 22.9855 15.0118 22.9855 20.1208V20.64ZM1.10675 19.6015H21.9296C21.6612 15.308 18.0822 11.8962 13.7224 11.8962H9.3139C4.95417 11.8962 1.37509 15.308 1.10675 19.6015Z" fill="black"/>
                <path d="M11.5196 11.8963C8.2542 11.8963 5.59863 9.24071 5.59863 5.97527C5.59863 2.70983 8.2542 0.0507812 11.5196 0.0507812C14.7851 0.0507812 17.4406 2.70635 17.4406 5.97179C17.4406 9.23723 14.7851 11.8928 11.5196 11.8928V11.8963ZM11.5196 1.09279C8.82574 1.09279 6.63715 3.28137 6.63715 5.97527C6.63715 8.66918 8.82922 10.8578 11.5196 10.8578C14.2101 10.8578 16.4021 8.66918 16.4021 5.97527C16.4021 3.28137 14.2135 1.09279 11.5196 1.09279Z" fill="black"/>
                <path d="M22.9855 20.64H0.0507812V20.1208C0.0507812 15.0118 4.2049 10.8577 9.3139 10.8577H13.7224C18.8314 10.8577 22.9855 15.0118 22.9855 20.1208V20.64ZM1.10675 19.6015H21.9296C21.6612 15.308 18.0822 11.8962 13.7224 11.8962H9.3139C4.95417 11.8962 1.37509 15.308 1.10675 19.6015Z" stroke="black" strokeWidth="0.1"/>
                <path d="M11.5196 11.8963C8.2542 11.8963 5.59863 9.24071 5.59863 5.97527C5.59863 2.70983 8.2542 0.0507812 11.5196 0.0507812C14.7851 0.0507812 17.4406 2.70635 17.4406 5.97179C17.4406 9.23723 14.7851 11.8928 11.5196 11.8928V11.8963ZM11.5196 1.09279C8.82574 1.09279 6.63715 3.28137 6.63715 5.97527C6.63715 8.66918 8.82922 10.8578 11.5196 10.8578C14.2101 10.8578 16.4021 8.66918 16.4021 5.97527C16.4021 3.28137 14.2135 1.09279 11.5196 1.09279Z" stroke="black" strokeWidth="0.1"/>
                </svg>
              ) : (
                <svg xmlns="http://www.w3.org/2000/svg" width="23" height="21" viewBox="0 0 23 21" fill="none">
                <path d="M22.9348 20.5893H0V20.07C0 14.961 4.15412 10.8069 9.26312 10.8069H13.6716C18.7807 10.8069 22.9348 14.961 22.9348 20.07V20.5893ZM1.05596 19.5507H21.8788C21.6105 15.2572 18.0314 11.8454 13.6716 11.8454H9.26312C4.90339 11.8454 1.32431 15.2572 1.05596 19.5507Z" fill="#77808A"/>
                <path d="M11.4689 11.8455C8.20341 11.8455 5.54785 9.18993 5.54785 5.92449C5.54785 2.65905 8.20341 0 11.4689 0C14.7343 0 17.3899 2.65557 17.3899 5.92101C17.3899 9.18645 14.7343 11.842 11.4689 11.842V11.8455ZM11.4689 1.04201C8.77495 1.04201 6.58637 3.23059 6.58637 5.92449C6.58637 8.61839 8.77844 10.807 11.4689 10.807C14.1593 10.807 16.3513 8.61839 16.3513 5.92449C16.3513 3.23059 14.1628 1.04201 11.4689 1.04201Z" fill="#77808A"/>
                </svg>
              )}
            <span className={activeButton === "signIn" ? classes.activeText : classes.nonActiveText}>
              Anmelden
            </span>
            </div>
        </div>
      </header>
    ) : (
      <header className={classes.headerLogin} style={fixedHeaderStyle}>
        {activeView === "explorer" ? (
        <div className={classes.headerLoginContent}>
        <Link to={activeButton !== "search" ? "/search" : "#"} onClick={(e) => {
          if (activeButton === "search") {
            e.preventDefault();
          }
        }}>
          <div
            className={classes.navigationButtons}
            onClick={() => handleButtonClick("search")}
          >
            <div className={`${classes.navigationButtonsInner} ${activeButton === "search" ? classes.activeButton : ''}`}>
              {activeButton === "search" ? (
                <svg xmlns="http://www.w3.org/2000/svg" width="22" height="22" viewBox="0 0 22 22" fill="none">
                  <path d="M14.8794 14.0012C16.1584 12.5236 16.9321 10.5964 16.9321 8.49144C16.9321 3.83549 13.1474 0.0507812 8.49144 0.0507812C3.83549 0.0507812 0.0507812 3.83549 0.0507812 8.49144C0.0507812 13.1474 3.83549 16.9321 8.49144 16.9321C10.5964 16.9321 12.5236 16.1584 14.0012 14.8794L20.783 21.4312L21.6438 20.5426L14.876 14.0012H14.8794ZM8.49144 15.6949C4.51855 15.6949 1.28447 12.4608 1.28447 8.48796C1.28447 4.51506 4.51855 1.28795 8.49144 1.28795C12.4643 1.28795 15.6984 4.52203 15.6984 8.49492C15.6984 12.4678 12.4643 15.7019 8.49144 15.7019V15.6949Z" fill="black" stroke="black" strokeWidth="0.1"/>
                </svg>
              ) : (
                <svg xmlns="http://www.w3.org/2000/svg" width="22" height="22" viewBox="0 0 22 22" fill="none">
                  <path d="M14.8287 13.9504C16.1076 12.4728 16.8813 10.5456 16.8813 8.44066C16.8813 3.7847 13.0966 0 8.44066 0C3.7847 0 0 3.7847 0 8.44066C0 13.0966 3.7847 16.8813 8.44066 16.8813C10.5456 16.8813 12.4728 16.1076 13.9504 14.8287L20.7322 21.3804L21.593 20.4918L14.8252 13.9504H14.8287ZM8.44066 15.6441C4.46776 15.6441 1.23369 12.4101 1.23369 8.43717C1.23369 4.46428 4.46776 1.23717 8.44066 1.23717C12.4136 1.23717 15.6476 4.47125 15.6476 8.44414C15.6476 12.417 12.4136 15.6511 8.44066 15.6511V15.6441Z" fill="#77808A"/>
                </svg>
              )}
            </div>
            <span className={activeButton === "search" ? classes.activeText : classes.nonActiveText}>
              Suchen
            </span>
          </div>
        </Link>
        <Link to={activeButton !== "favourite" ? "/wishlists" : "#"} onClick={(e) => {
          if (activeButton === "favourite") {
            e.preventDefault();
          }
        }}>
          <div
            className={classes.navigationButtons}
            onClick={() => handleButtonClick("favourite")}
          >
            <div className={`${classes.navigationButtonsInner} ${activeButton === "favourite" ? classes.activeButton : ''}`}>
              {activeButton === "favourite" ? (
                <svg xmlns="http://www.w3.org/2000/svg" width="24" height="21" viewBox="0 0 24 21" fill="none">
                  <path d="M11.7071 20.4026L11.3935 20.2005C7.69241 17.8028 4.70577 15.3599 2.9807 13.3281C2.32552 12.5544 1.70867 11.6797 1.04652 10.5715C0.433163 9.55037 -0.877194 5.96082 1.10228 2.99161C2.33249 1.14456 4.51061 -0.0159444 6.67828 0.0537555C8.21864 0.0990604 9.68931 0.72636 11.045 1.91823C11.2785 2.12036 11.5015 2.3434 11.7106 2.57689C11.9197 2.3434 12.1428 2.12036 12.3797 1.91474C13.7319 0.726359 15.1991 0.0990604 16.7395 0.0537555C18.8932 -0.00200444 21.0852 1.14456 22.3189 2.99161C24.6051 6.42084 22.4792 10.3763 22.3852 10.5506C21.615 11.9515 20.9005 12.8123 20.4301 13.3386C19.294 14.6036 16.6907 17.1755 12.0277 20.197L11.7141 20.3992L11.7071 20.4026ZM6.50403 1.20729C4.78244 1.20729 3.05388 2.14824 2.06415 3.63285C0.133458 6.52888 2.01884 9.94417 2.03975 9.97554C2.67053 11.028 3.25253 11.854 3.86589 12.5788C4.80335 13.6836 7.05466 15.9697 11.7106 19.0261C16.0669 16.1649 18.4959 13.7637 19.5693 12.5684C20.0014 12.084 20.6601 11.2929 21.371 9.99645C21.3884 9.9616 23.2599 6.48706 21.3571 3.63285C20.3395 2.1099 18.5482 1.16198 16.7743 1.21077C15.5093 1.24911 14.286 1.77883 13.1395 2.78948C12.7805 3.10313 12.4564 3.45511 12.1811 3.83846L11.7176 4.4797L11.2471 3.84195C10.9544 3.44466 10.6372 3.09964 10.2818 2.78948C9.13171 1.77883 7.90848 1.24911 6.64343 1.21077C6.59812 1.21077 6.54933 1.21077 6.50403 1.21077V1.20729Z" fill="black" stroke="black" strokeWidth="0.1"/>
                </svg>
              ) : (
                <svg xmlns="http://www.w3.org/2000/svg" width="24" height="21" viewBox="0 0 24 21" fill="none">
                  <path d="M11.6563 20.3519L11.3427 20.1497C7.64163 17.7521 4.65499 15.3091 2.92991 13.2773C2.27474 12.5037 1.65789 11.6289 0.995741 10.5207C0.382382 9.49959 -0.927975 5.91004 1.0515 2.94083C2.28171 1.09378 4.45983 -0.0667257 6.62749 0.00297423C8.16786 0.0482792 9.63853 0.675578 10.9942 1.86745C11.2277 2.06958 11.4507 2.29262 11.6598 2.52611C11.8689 2.29262 12.092 2.06958 12.329 1.86396C13.6811 0.675578 15.1483 0.0482792 16.6887 0.00297423C18.8424 -0.0527857 21.0345 1.09378 22.2682 2.94083C24.5543 6.37006 22.4285 10.3255 22.3344 10.4998C21.5642 11.9007 20.8498 12.7615 20.3793 13.2878C19.2432 14.5528 16.6399 17.1248 11.977 20.1462L11.6633 20.3484L11.6563 20.3519ZM6.45324 1.15651C4.73166 1.15651 3.0031 2.09746 2.01336 3.58206C0.0826769 6.4781 1.96806 9.89339 1.98897 9.92476C2.61975 10.9772 3.20175 11.8032 3.81511 12.528C4.75257 13.6328 7.00387 15.9189 11.6598 18.9753C16.0161 16.1141 18.4451 13.7129 19.5185 12.5176C19.9506 12.0332 20.6093 11.2421 21.3202 9.94567C21.3377 9.91082 23.2091 6.43628 21.3063 3.58206C20.2887 2.05912 18.4974 1.1112 16.7235 1.15999C15.4585 1.19833 14.2352 1.72805 13.0887 2.7387C12.7297 3.05235 12.4056 3.40433 12.1303 3.78768L11.6668 4.42892L11.1963 3.79116C10.9036 3.39387 10.5864 3.04886 10.231 2.7387C9.08093 1.72805 7.8577 1.19833 6.59265 1.15999C6.54734 1.15999 6.49855 1.15999 6.45324 1.15999V1.15651Z" fill="#77808A"/>
                </svg>
              )}
            </div>
              <span className={activeButton === "favourite" ? classes.activeText : classes.nonActiveText}>
              Wunschliste
            </span>
          </div>
        </Link>
        <Link to={activeButton !== "bookings" ? "/bookings" : "#"}
          className={classes.navigationButtons}
          onClick={(e) => {
            if (activeButton === "bookings") {
              e.preventDefault();
            }
            handleButtonClick("bookings");
          }}
        >
          <div className={`${classes.navigationButtonsInner} ${activeButton === "bookings" ? classes.activeButton : ''}`}>
            {activeButton === "bookings" ? (
              <svg xmlns="http://www.w3.org/2000/svg" width="22" height="20" viewBox="0 0 22 20" fill="none">
                <path d="M14.7818 7.85708L9.02807 13.7363L8.72836 14.036L8.43214 13.7363L6.29932 11.6035L6.59554 11.3037L8.72836 13.44L14.4821 7.55737L14.7818 7.85708Z" fill="black"/>
                <path d="M8.72838 14.4438L5.8916 11.6035L6.59557 10.896L8.72838 13.0288L14.4821 7.14966L15.1861 7.85363L8.72838 14.4438Z" fill="black"/>
                <path d="M19.4622 19.835H1.61554C0.754747 19.835 0.0507812 19.1345 0.0507812 18.2702V2.86652C0.0507812 2.00224 0.754747 1.30176 1.61554 1.30176H19.4622C20.323 1.30176 21.027 2.00573 21.027 2.86652V18.2702C21.027 19.1345 20.323 19.835 19.4622 19.835ZM1.61554 2.45529C1.38902 2.45529 1.20431 2.64 1.20431 2.86652V18.2702C1.20431 18.4967 1.38902 18.6814 1.61554 18.6814H19.4622C19.6887 18.6814 19.8734 18.4967 19.8734 18.2702V2.86652C19.8734 2.64 19.6887 2.45529 19.4622 2.45529H1.61554Z" fill="black"/>
                <path d="M11.1157 0.0507812H9.96216V3.58805H11.1157V0.0507812Z" fill="black"/>
                <path d="M16.0363 0.0507812H14.8828V3.58805H16.0363V0.0507812Z" fill="black"/>
                <path d="M6.20871 0.0507812H5.05518V3.58805H6.20871V0.0507812Z" fill="black"/>
                <path d="M14.7818 7.85708L9.02807 13.7363L8.72836 14.036L8.43214 13.7363L6.29932 11.6035L6.59554 11.3037L8.72836 13.44L14.4821 7.55737L14.7818 7.85708Z" stroke="black" strokeWidth="0.1"/>
                <path d="M8.72838 14.4438L5.8916 11.6035L6.59557 10.896L8.72838 13.0288L14.4821 7.14966L15.1861 7.85363L8.72838 14.4438Z" stroke="black" strokeWidth="0.1"/>
                <path d="M19.4622 19.835H1.61554C0.754747 19.835 0.0507812 19.1345 0.0507812 18.2702V2.86652C0.0507812 2.00224 0.754747 1.30176 1.61554 1.30176H19.4622C20.323 1.30176 21.027 2.00573 21.027 2.86652V18.2702C21.027 19.1345 20.323 19.835 19.4622 19.835ZM1.61554 2.45529C1.38902 2.45529 1.20431 2.64 1.20431 2.86652V18.2702C1.20431 18.4967 1.38902 18.6814 1.61554 18.6814H19.4622C19.6887 18.6814 19.8734 18.4967 19.8734 18.2702V2.86652C19.8734 2.64 19.6887 2.45529 19.4622 2.45529H1.61554Z" stroke="black" strokeWidth="0.1"/>
                <path d="M11.1157 0.0507812H9.96216V3.58805H11.1157V0.0507812Z" stroke="black" strokeWidth="0.1"/>
                <path d="M16.0363 0.0507812H14.8828V3.58805H16.0363V0.0507812Z" stroke="black" strokeWidth="0.1"/>
                <path d="M6.20871 0.0507812H5.05518V3.58805H6.20871V0.0507812Z" stroke="black" strokeWidth="0.1"/>
              </svg>
            ) : (
              <svg xmlns="http://www.w3.org/2000/svg" width="21" height="20" viewBox="0 0 21 20" fill="none">
                <path d="M14.731 7.8063L8.97729 13.6855L8.67758 13.9852L8.38136 13.6855L6.24854 11.5527L6.54476 11.253L8.67758 13.3893L14.4313 7.50659L14.731 7.8063Z" fill="#77808A"/>
                <path d="M8.6776 14.393L5.84082 11.5527L6.54479 10.8452L8.6776 12.9781L14.4313 7.09888L15.1353 7.80285L8.6776 14.393Z" fill="#77808A"/>
                <path d="M19.4114 19.7842H1.56476C0.703966 19.7842 0 19.0837 0 18.2194V2.81574C0 1.95146 0.703966 1.25098 1.56476 1.25098H19.4114C20.2722 1.25098 20.9762 1.95495 20.9762 2.81574V18.2194C20.9762 19.0837 20.2722 19.7842 19.4114 19.7842ZM1.56476 2.40451C1.33823 2.40451 1.15353 2.58922 1.15353 2.81574V18.2194C1.15353 18.4459 1.33823 18.6306 1.56476 18.6306H19.4114C19.638 18.6306 19.8227 18.4459 19.8227 18.2194V2.81574C19.8227 2.58922 19.638 2.40451 19.4114 2.40451H1.56476Z" fill="#77808A"/>
                <path d="M11.0649 0H9.91138V3.53727H11.0649V0Z" fill="#77808A"/>
                <path d="M15.9856 0H14.832V3.53727H15.9856V0Z" fill="#77808A"/>
                <path d="M6.15793 0H5.00439V3.53727H6.15793V0Z" fill="#77808A"/>
              </svg>
            )}
            {bookingNotifications > 0 && (
              <svg 
                xmlns="http://www.w3.org/2000/svg" 
                width="8" 
                height="8" 
                viewBox="0 0 8 8" 
                fill="none"
                className={classes.notificationIcon}
              >
                <path d="M4.00005 8C6.20928 8 8.00009 6.20916 8.00009 4.00002C8.00009 1.79087 6.20928 0 4.00005 0C1.79095 0 0 1.79087 0 4.00002C0 6.20916 1.79095 8 4.00005 8Z" fill="#DF315A"/>
              </svg>
            )}
          </div>
          <span className={activeButton === "bookings" ? classes.activeText : classes.nonActiveText}>
            Buchungen
          </span>
        </Link>
        <Link to="/messages">
          <div
            className={`${classes.navigationButtons} ${activeButton === "messages" ? classes.activeButton : ''}`}
            style={{ position: 'relative' }}
            onClick={() => handleButtonClick("messages")}
          >
            <div className={`${classes.navigationButtonsInner} ${activeButton === "messages" ? classes.activeButton : ''}`}>
              {activeButton === "messages" ? (
                <svg xmlns="http://www.w3.org/2000/svg" width="22" height="21" viewBox="0 0 22 21" fill="none">
                  <path d="M0.0683594 20.4415L1.77602 14.9491C0.838552 13.4366 0.343666 11.7325 0.343666 10.0004C0.343666 4.51506 5.1913 0.0507812 11.1506 0.0507812C17.11 0.0507812 21.9576 4.51158 21.9576 10.0004C21.9576 15.4893 17.11 19.9362 11.1506 19.9362C9.31404 19.9362 7.50185 19.504 5.90224 18.6851L0.0718478 20.4415H0.0683594ZM6.03816 17.4514L6.24378 17.5629C7.72839 18.3644 9.42557 18.7861 11.1541 18.7861C16.4757 18.7861 20.8041 14.8446 20.8041 10.0039C20.8041 5.16327 16.4757 1.21128 11.1541 1.21128C5.83254 1.21128 1.50069 5.1563 1.50069 10.0039C1.50069 11.5966 1.97814 13.1648 2.88424 14.5414L3.02712 14.7609L1.71676 18.7896L6.03467 17.4548L6.03816 17.4514Z" fill="black" stroke="black" strokeWidth="0.1"/>
                </svg>
              ) : (
                <svg xmlns="http://www.w3.org/2000/svg" width="22" height="21" viewBox="0 0 22 21" fill="none">
                  <path d="M0.0034884 20.3907L1.71114 14.8984C0.773681 13.3859 0.278795 11.6817 0.278795 9.94966C0.278795 4.46428 5.12643 0 11.0858 0C17.0451 0 21.8927 4.46079 21.8927 9.94966C21.8927 15.4385 17.0451 19.8854 11.0858 19.8854C9.24918 19.8854 7.43698 19.4532 5.83737 18.6343L0 20.3907H0.0034884ZM5.97329 17.4006L6.17891 17.5121C7.66352 18.3137 9.3607 18.7353 11.0893 18.7353C16.4108 18.7353 20.7392 14.7938 20.7392 9.95315C20.7392 5.11249 16.4108 1.1605 11.0893 1.1605C5.76767 1.1605 1.43582 5.10552 1.43582 9.95315C1.43582 11.5458 1.91327 13.114 2.81937 14.4906L2.96225 14.7102L1.65188 18.7388L5.9698 17.4041L5.97329 17.4006Z" fill="#77808A"/>
                </svg>
              )}
              {unreadCount > 0 && (
                <svg 
                  xmlns="http://www.w3.org/2000/svg" 
                  width="8" 
                  height="8" 
                  viewBox="0 0 8 8" 
                  fill="none"
                  className={classes.notificationIcon}	
                >
                  <path d="M4.00005 8C6.20928 8 8.00009 6.20916 8.00009 4.00002C8.00009 1.79087 6.20928 0 4.00005 0C1.79095 0 0 1.79087 0 4.00002C0 6.20916 1.79095 8 4.00005 8Z" fill="#DF315A"/>
                </svg>
              )}
            </div>
            <span className={activeButton === "messages" ? classes.activeText : classes.nonActiveText}>
              Posteingang
            </span>
          </div>
        </Link>
        <Link to="/profile">
          <div
            className={`${classes.navigationButtons} ${activeButton === "profile" ? classes.activeButton : ''}`}
            onClick={activeButton !== "profile" ? handleProfileClick : undefined}
          >
            <div className={`${classes.navigationButtonsInner} ${activeButton === "profile" ? classes.activeButton : ''}`}>
            {activeButton === "profile" ? (
                <svg xmlns="http://www.w3.org/2000/svg" width="24" height="21" viewBox="0 0 24 21" fill="none">
                <path d="M22.9855 20.64H0.0507812V20.1208C0.0507812 15.0118 4.2049 10.8577 9.3139 10.8577H13.7224C18.8314 10.8577 22.9855 15.0118 22.9855 20.1208V20.64ZM1.10675 19.6015H21.9296C21.6612 15.308 18.0822 11.8962 13.7224 11.8962H9.3139C4.95417 11.8962 1.37509 15.308 1.10675 19.6015Z" fill="black"/>
                <path d="M11.5196 11.8963C8.2542 11.8963 5.59863 9.24071 5.59863 5.97527C5.59863 2.70983 8.2542 0.0507812 11.5196 0.0507812C14.7851 0.0507812 17.4406 2.70635 17.4406 5.97179C17.4406 9.23723 14.7851 11.8928 11.5196 11.8928V11.8963ZM11.5196 1.09279C8.82574 1.09279 6.63715 3.28137 6.63715 5.97527C6.63715 8.66918 8.82922 10.8578 11.5196 10.8578C14.2101 10.8578 16.4021 8.66918 16.4021 5.97527C16.4021 3.28137 14.2135 1.09279 11.5196 1.09279Z" fill="black"/>
                <path d="M22.9855 20.64H0.0507812V20.1208C0.0507812 15.0118 4.2049 10.8577 9.3139 10.8577H13.7224C18.8314 10.8577 22.9855 15.0118 22.9855 20.1208V20.64ZM1.10675 19.6015H21.9296C21.6612 15.308 18.0822 11.8962 13.7224 11.8962H9.3139C4.95417 11.8962 1.37509 15.308 1.10675 19.6015Z" stroke="black" strokeWidth="0.1"/>
                <path d="M11.5196 11.8963C8.2542 11.8963 5.59863 9.24071 5.59863 5.97527C5.59863 2.70983 8.2542 0.0507812 11.5196 0.0507812C14.7851 0.0507812 17.4406 2.70635 17.4406 5.97179C17.4406 9.23723 14.7851 11.8928 11.5196 11.8928V11.8963ZM11.5196 1.09279C8.82574 1.09279 6.63715 3.28137 6.63715 5.97527C6.63715 8.66918 8.82922 10.8578 11.5196 10.8578C14.2101 10.8578 16.4021 8.66918 16.4021 5.97527C16.4021 3.28137 14.2135 1.09279 11.5196 1.09279Z" stroke="black" strokeWidth="0.1"/>
                </svg>
              ) : (
                <svg xmlns="http://www.w3.org/2000/svg" width="23" height="21" viewBox="0 0 23 21" fill="none">
                <path d="M22.9348 20.5893H0V20.07C0 14.961 4.15412 10.8069 9.26312 10.8069H13.6716C18.7807 10.8069 22.9348 14.961 22.9348 20.07V20.5893ZM1.05596 19.5507H21.8788C21.6105 15.2572 18.0314 11.8454 13.6716 11.8454H9.26312C4.90339 11.8454 1.32431 15.2572 1.05596 19.5507Z" fill="#77808A"/>
                <path d="M11.4689 11.8455C8.20341 11.8455 5.54785 9.18993 5.54785 5.92449C5.54785 2.65905 8.20341 0 11.4689 0C14.7343 0 17.3899 2.65557 17.3899 5.92101C17.3899 9.18645 14.7343 11.842 11.4689 11.842V11.8455ZM11.4689 1.04201C8.77495 1.04201 6.58637 3.23059 6.58637 5.92449C6.58637 8.61839 8.77844 10.807 11.4689 10.807C14.1593 10.807 16.3513 8.61839 16.3513 5.92449C16.3513 3.23059 14.1628 1.04201 11.4689 1.04201Z" fill="#77808A"/>
                </svg>
              )}
            </div>
            <span className={activeButton === "profile" ? classes.activeText : classes.nonActiveText}>
              Profil
            </span>
          </div>
        </Link>
        </div>
        ) : (
          <div className={classes.headerLoginContent}>
          <Link to={activeButton !== "servicesStudios" ? "/services-studios" : "#"} onClick={(e) => {
            if (activeButton === "servicesStudios") {
              e.preventDefault();
            }
          }}>
            <div
              className={classes.navigationButtons}
              onClick={() => handleButtonClick("servicesStudios")}
            >
              <div className={`${classes.navigationButtonsInner} ${activeButton === "servicesStudios" ? classes.activeButton : ''}`}>
              {activeButton === "servicesStudios" ? (
                  <svg xmlns="http://www.w3.org/2000/svg" width="23" height="21" viewBox="0 0 23 21" fill="none">
                  <g clipPath="url(#clip0_16945_36815)">
                  <path d="M21.4033 2.3584C21.4033 1.99952 21.1104 1.70898 20.7529 1.70898H5.67871C5.31983 1.70898 5.02832 2.00096 5.02832 2.3584V5.13281H5.02539V15.499C5.02558 15.8577 5.31748 16.1484 5.6748 16.1484H20.7529C21.1117 16.1484 21.4031 15.8563 21.4033 15.499V2.3584ZM22.4902 15.499C22.49 16.4547 21.7143 17.2354 20.7529 17.2354H5.6748C4.71904 17.2354 3.93769 16.4604 3.9375 15.499V5.13281H3.94141V2.3584C3.94141 1.40251 4.7172 0.621094 5.67871 0.621094H20.7529C21.7088 0.621094 22.4902 1.39689 22.4902 2.3584V15.499Z" fill="black"/>
                  <path d="M3.83594 15.501V5.03516H3.83984V2.36035C3.83984 1.34945 4.65981 0.523438 5.67676 0.523438V0.623047C4.71525 0.623047 3.93945 1.40446 3.93945 2.36035V5.13477H3.93555V15.501C3.93574 16.4623 4.71708 17.2373 5.67285 17.2373H20.751C21.7124 17.2373 22.4881 16.4567 22.4883 15.501V2.36035C22.4883 1.39884 21.7069 0.623047 20.751 0.623047V0.523438C21.7619 0.523438 22.5879 1.3434 22.5879 2.36035V15.501C22.5877 16.5118 21.7678 17.3369 20.751 17.3369H5.67285C4.66212 17.3369 3.83614 16.5178 3.83594 15.501ZM20.751 16.0508V16.1504H5.67285V16.0508H20.751ZM21.3018 15.501V2.36035C21.3018 2.05704 21.0535 1.81055 20.751 1.81055H5.67676C5.37306 1.81055 5.12598 2.05819 5.12598 2.36035V5.23438H5.12305V15.501C5.12324 15.8044 5.37069 16.0508 5.67285 16.0508V16.1504C5.31553 16.1504 5.02363 15.8597 5.02344 15.501V5.13477H5.02637V2.36035C5.02637 2.00291 5.31788 1.71094 5.67676 1.71094H20.751C21.1084 1.71094 21.4014 2.00147 21.4014 2.36035V15.501C21.4012 15.8583 21.1097 16.1504 20.751 16.1504V16.0508C21.0165 16.0508 21.2385 15.861 21.29 15.6113L21.3018 15.501ZM20.751 0.523438V0.623047H5.67676V0.523438H20.751Z" fill="black"/>
                  <path d="M0.693359 18.8398V5.31152C0.693403 4.3247 1.49462 3.52348 2.48145 3.52344H2.94043V4.64258H2.48145C2.11271 4.64262 1.81254 4.94279 1.8125 5.31152V18.8398C1.8125 19.2086 2.11268 19.5097 2.48145 19.5098H18.001C18.3698 19.5098 18.6699 19.2086 18.6699 18.8398V18.208H19.79V18.8398C19.79 19.8267 18.9879 20.6289 18.001 20.6289H2.48145C1.49459 20.6289 0.693359 19.8267 0.693359 18.8398Z" fill="black"/>
                  <path d="M0.59375 18.8379V5.30957C0.593796 4.26752 1.43939 3.42192 2.48145 3.42188V3.52148C1.49462 3.52153 0.693403 4.32274 0.693359 5.30957V18.8379C0.693359 19.8248 1.49459 20.6269 2.48145 20.627H18.001C18.9879 20.627 19.79 19.8248 19.79 18.8379V18.2061H18.6699V18.8379C18.6699 19.2067 18.3698 19.5078 18.001 19.5078V19.4082C18.3144 19.4082 18.5703 19.1517 18.5703 18.8379V18.1064H19.8896V18.8379C19.8896 19.88 19.0431 20.7266 18.001 20.7266H2.48145C1.4393 20.7265 0.59375 19.8799 0.59375 18.8379ZM18.001 19.4082V19.5078H2.48145V19.4082H18.001ZM1.8125 18.8379V5.30957C1.81254 4.94083 2.11271 4.64067 2.48145 4.64062H2.94043V3.52148H2.48145V3.42188H3.04004V4.74023H2.48145C2.16794 4.74028 1.91215 4.99606 1.91211 5.30957V18.8379C1.91211 19.1516 2.16808 19.4082 2.48145 19.4082V19.5078C2.11268 19.5078 1.8125 19.2067 1.8125 18.8379Z" fill="black"/>
                  </g>
                  <defs>
                  <clipPath id="clip0_16945_36815">
                  <rect width="23" height="21" fill="white"/>
                  </clipPath>
                  </defs>
                  </svg>
              ) : (
                <svg xmlns="http://www.w3.org/2000/svg" width="23" height="21" viewBox="0 0 23 21" fill="none">
                <g clipPath="url(#clip0_16945_36806)">
                <path d="M21.4033 2.3584C21.4033 1.99952 21.1104 1.70898 20.7529 1.70898H5.67871C5.31983 1.70898 5.02832 2.00096 5.02832 2.3584V5.13281H5.02539V15.499C5.02558 15.8577 5.31748 16.1484 5.6748 16.1484H20.7529C21.1117 16.1484 21.4031 15.8563 21.4033 15.499V2.3584ZM22.4902 15.499C22.49 16.4547 21.7143 17.2354 20.7529 17.2354H5.6748C4.71904 17.2354 3.93769 16.4604 3.9375 15.499V5.13281H3.94141V2.3584C3.94141 1.40251 4.7172 0.621094 5.67871 0.621094H20.7529C21.7088 0.621094 22.4902 1.39689 22.4902 2.3584V15.499Z" fill="#77808A"/>
                <path d="M0.693359 18.8398V5.31152C0.693403 4.3247 1.49462 3.52348 2.48145 3.52344H2.94043V4.64258H2.48145C2.11271 4.64262 1.81254 4.94279 1.8125 5.31152V18.8398C1.8125 19.2086 2.11268 19.5097 2.48145 19.5098H18.001C18.3698 19.5098 18.6699 19.2086 18.6699 18.8398V18.208H19.79V18.8398C19.79 19.8267 18.9879 20.6289 18.001 20.6289H2.48145C1.49459 20.6289 0.693359 19.8267 0.693359 18.8398Z" fill="#77808A"/>
                </g>
                <defs>
                <clipPath id="clip0_16945_36806">
                <rect width="23" height="21" fill="white"/>
                </clipPath>
                </defs>
                </svg>
              )}
              </div>
              <span className={activeButton === "servicesStudios" ? classes.activeText : classes.nonActiveText}>
                Studios
              </span>
            </div>
          </Link>
          <Link to={activeButton !== "calendar" ? "/calendar" : "#"} onClick={(e) => {
            if (activeButton === "calendar") {
              e.preventDefault();
            }
          }}>
            <div
              className={classes.navigationButtons}
              onClick={() => handleButtonClick("calendar")}
            >
              <div className={`${classes.navigationButtonsInner} ${activeButton === "calendar" ? classes.activeButton : ''}`}>
              {activeButton === "calendar" ? (
                <svg xmlns="http://www.w3.org/2000/svg" width="22" height="20" viewBox="0 0 22 20" fill="none">
                <path d="M19.4622 19.835H1.61554C0.754747 19.835 0.0507812 19.1345 0.0507812 18.2702V2.86652C0.0507812 2.00224 0.754747 1.30176 1.61554 1.30176H19.4622C20.323 1.30176 21.027 2.00573 21.027 2.86652V18.2702C21.027 19.1345 20.323 19.835 19.4622 19.835ZM1.61554 2.45529C1.38902 2.45529 1.20431 2.64 1.20431 2.86652V18.2702C1.20431 18.4967 1.38902 18.6814 1.61554 18.6814H19.4622C19.6887 18.6814 19.8734 18.4967 19.8734 18.2702V2.86652C19.8734 2.64 19.6887 2.45529 19.4622 2.45529H1.61554Z" fill="black"/>
                <path d="M11.1157 0.0507812H9.96216V3.58805H11.1157V0.0507812Z" fill="black"/>
                <path d="M16.0363 0.0507812H14.8828V3.58805H16.0363V0.0507812Z" fill="black"/>
                <path d="M6.20871 0.0507812H5.05518V3.58805H6.20871V0.0507812Z" fill="black"/>
                <path d="M19.4622 19.835H1.61554C0.754747 19.835 0.0507812 19.1345 0.0507812 18.2702V2.86652C0.0507812 2.00224 0.754747 1.30176 1.61554 1.30176H19.4622C20.323 1.30176 21.027 2.00573 21.027 2.86652V18.2702C21.027 19.1345 20.323 19.835 19.4622 19.835ZM1.61554 2.45529C1.38902 2.45529 1.20431 2.64 1.20431 2.86652V18.2702C1.20431 18.4967 1.38902 18.6814 1.61554 18.6814H19.4622C19.6887 18.6814 19.8734 18.4967 19.8734 18.2702V2.86652C19.8734 2.64 19.6887 2.45529 19.4622 2.45529H1.61554Z" stroke="black" strokeWidth="0.1"/>
                <path d="M11.1157 0.0507812H9.96216V3.58805H11.1157V0.0507812Z" stroke="black" strokeWidth="0.1"/>
                <path d="M16.0363 0.0507812H14.8828V3.58805H16.0363V0.0507812Z" stroke="black" strokeWidth="0.1"/>
                <path d="M6.20871 0.0507812H5.05518V3.58805H6.20871V0.0507812Z" stroke="black" strokeWidth="0.1"/>
                </svg>
              ) : ( 
                <svg xmlns="http://www.w3.org/2000/svg" width="21" height="20" viewBox="0 0 21 20" fill="none">
                <path d="M19.4114 19.7842H1.56476C0.703966 19.7842 0 19.0837 0 18.2194V2.81574C0 1.95146 0.703966 1.25098 1.56476 1.25098H19.4114C20.2722 1.25098 20.9762 1.95495 20.9762 2.81574V18.2194C20.9762 19.0837 20.2722 19.7842 19.4114 19.7842ZM1.56476 2.40451C1.33823 2.40451 1.15353 2.58922 1.15353 2.81574V18.2194C1.15353 18.4459 1.33823 18.6306 1.56476 18.6306H19.4114C19.638 18.6306 19.8227 18.4459 19.8227 18.2194V2.81574C19.8227 2.58922 19.638 2.40451 19.4114 2.40451H1.56476Z" fill="#77808A"/>
                <path d="M11.0649 0H9.91138V3.53727H11.0649V0Z" fill="#77808A"/>
                <path d="M15.9856 0H14.832V3.53727H15.9856V0Z" fill="#77808A"/>
                <path d="M6.15793 0H5.00439V3.53727H6.15793V0Z" fill="#77808A"/>
                </svg>
              )}
                </div>
                <span className={activeButton === "calendar" ? classes.activeText : classes.nonActiveText}>
                  Kalender
                </span>
            </div>
          </Link>
          <Link to={activeButton !== "bookings" ? "/creator" : "#"} onClick={(e) => {
            if (activeButton === "bookings") {
              e.preventDefault();
            }
          }}>
            <div
              className={classes.navigationButtons}
              onClick={() => handleButtonClick("bookings")}
            >
              <div className={`${classes.navigationButtonsInner} ${activeButton === "bookings" ? classes.activeButton : ''}`}>
              {activeButton === "bookings" ? (
                  <svg xmlns="http://www.w3.org/2000/svg" width="21" height="21" viewBox="0 0 21 21" fill="none">
                  <path d="M0.329403 8.00151C1.64065 2.45424 7.19915 -0.981091 12.7435 0.32964C13.4513 0.4962 14.1215 0.733733 14.7513 1.02788L14.5198 1.52202L14.2894 2.01616C13.8649 1.81789 13.4223 1.64812 12.9612 1.51323L12.4944 1.39019C7.53631 0.217404 2.56305 3.28925 1.38995 8.25151C0.217219 13.2128 3.29671 18.1829 8.25616 19.356C13.2142 20.5286 18.1867 17.455 19.3597 12.4927C19.5423 11.7249 19.6236 10.9553 19.6067 10.2007L20.1507 10.188L20.6956 10.1763C20.7146 11.0254 20.6241 11.8885 20.4202 12.7457C19.1084 18.2926 13.5494 21.7278 8.00518 20.4166C2.46184 19.1053 -0.98193 13.5502 0.329403 8.00151ZM18.9427 5.44194L10.2855 14.1197L5.138 8.97319L5.52374 8.58745L5.90948 8.20269L10.2835 12.5767L18.1712 4.67241L18.9427 5.44194Z" fill="black" stroke="black" strokeWidth="0.1" strokeMiterlimit="10"/>
                  </svg>
              ) : (
                  <svg xmlns="http://www.w3.org/2000/svg" width="21" height="22" viewBox="0 0 21 22" fill="none">
                  <g clipPath="url(#clip0_16949_36919)">
                  <path d="M20.0942 11.0834C20.1121 11.8852 20.0262 12.7014 19.8329 13.514C18.5907 18.7691 13.3249 22.0231 8.07348 20.7809C2.82201 19.5387 -0.439125 14.2765 0.803042 9.02146C2.04521 3.76641 7.311 0.512438 12.5625 1.7546C13.2319 1.91211 13.8655 2.13764 14.4633 2.41686" stroke="#77808A" strokeWidth="1.09" strokeMiterlimit="10"/>
                  <path d="M5.46484 9.48362L10.2259 14.2447L18.498 5.95312" stroke="#77808A" strokeWidth="1.09" strokeMiterlimit="10"/>
                  </g>
                  <defs>
                  <clipPath id="clip0_16949_36919">
                  <rect width="21" height="22" fill="white"/>
                  </clipPath>
                  </defs>
                  </svg>
                )}
                {bookingNotifications > 0 && (
                  <svg 
                    xmlns="http://www.w3.org/2000/svg" 
                    width="8" 
                    height="8" 
                    viewBox="0 0 8 8" 
                    fill="none"
                    className={classes.notificationIcon}
                  >
                    <path d="M4.00005 8C6.20928 8 8.00009 6.20916 8.00009 4.00002C8.00009 1.79087 6.20928 0 4.00005 0C1.79095 0 0 1.79087 0 4.00002C0 6.20916 1.79095 8 4.00005 8Z" fill="#DF315A"/>
                  </svg>
                )}
              </div>
              <span className={activeButton === "bookings" ? classes.activeText : classes.nonActiveText}>
                Buchungen
              </span>
            </div>
          </Link>
          <Link to="/messages" state={{ fromCreatorDetails: true }}>
            <div
              className={`${classes.navigationButtons} ${activeButton === "messages" ? classes.activeButton : ''}`}
              onClick={() => handleButtonClick("messages")}
            >
              <div className={`${classes.navigationButtonsInner} ${activeButton === "messages" ? classes.activeButton : ''}`}>
                {activeButton === "messages" ? (
                  <svg xmlns="http://www.w3.org/2000/svg" width="22" height="21" viewBox="0 0 22 21" fill="none">
                    <path d="M0.0683594 20.4415L1.77602 14.9491C0.838552 13.4366 0.343666 11.7325 0.343666 10.0004C0.343666 4.51506 5.1913 0.0507812 11.1506 0.0507812C17.11 0.0507812 21.9576 4.51158 21.9576 10.0004C21.9576 15.4893 17.11 19.9362 11.1506 19.9362C9.31404 19.9362 7.50185 19.504 5.90224 18.6851L0.0718478 20.4415H0.0683594ZM6.03816 17.4514L6.24378 17.5629C7.72839 18.3644 9.42557 18.7861 11.1541 18.7861C16.4757 18.7861 20.8041 14.8446 20.8041 10.0039C20.8041 5.16327 16.4757 1.21128 11.1541 1.21128C5.83254 1.21128 1.50069 5.1563 1.50069 10.0039C1.50069 11.5966 1.97814 13.1648 2.88424 14.5414L3.02712 14.7609L1.71676 18.7896L6.03467 17.4548L6.03816 17.4514Z" fill="black" stroke="black" strokeWidth="0.1"/>
                  </svg>
                ) : (
                  <svg xmlns="http://www.w3.org/2000/svg" width="22" height="21" viewBox="0 0 22 21" fill="none">
                    <path d="M0.0034884 20.3907L1.71114 14.8984C0.773681 13.3859 0.278795 11.6817 0.278795 9.94966C0.278795 4.46428 5.12643 0 11.0858 0C17.0451 0 21.8927 4.46079 21.8927 9.94966C21.8927 15.4385 17.0451 19.8854 11.0858 19.8854C9.24918 19.8854 7.43698 19.4532 5.83737 18.6343L0 20.3907H0.0034884ZM5.97329 17.4006L6.17891 17.5121C7.66352 18.3137 9.3607 18.7353 11.0893 18.7353C16.4108 18.7353 20.7392 14.7938 20.7392 9.95315C20.7392 5.11249 16.4108 1.1605 11.0893 1.1605C5.76767 1.1605 1.43582 5.10552 1.43582 9.95315C1.43582 11.5458 1.91327 13.114 2.81937 14.4906L2.96225 14.7102L1.65188 18.7388L5.9698 17.4041L5.97329 17.4006Z" fill="#77808A"/>
                  </svg>
                )}
                {unreadCount > 0 && (
                  <svg 
                    xmlns="http://www.w3.org/2000/svg" 
                    width="8" 
                    height="8" 
                    viewBox="0 0 8 8" 
                    fill="none"
                    className={classes.notificationIcon}
                  >
                    <path d="M4.00005 8C6.20928 8 8.00009 6.20916 8.00009 4.00002C8.00009 1.79087 6.20928 0 4.00005 0C1.79095 0 0 1.79087 0 4.00002C0 6.20916 1.79095 8 4.00005 8Z" fill="#DF315A"/>
                  </svg>
                )}
              </div>
              <span className={activeButton === "messages" ? classes.activeText : classes.nonActiveText}>
                Posteingang
              </span>
            </div>
          </Link>
          <Link to="/profile">
            <div
              className={`${classes.navigationButtons} ${activeButton === "profile" ? classes.activeButton : ''}`}
              onClick={activeButton !== "profile" ? handleProfileClick : undefined}
            >
              <div className={`${classes.navigationButtonsInner} ${activeButton === "profile" ? classes.activeButton : ''}`}>
              {activeButton === "profile" ? (
                <svg xmlns="http://www.w3.org/2000/svg" width="24" height="21" viewBox="0 0 24 21" fill="none">
                <path d="M22.9855 20.64H0.0507812V20.1208C0.0507812 15.0118 4.2049 10.8577 9.3139 10.8577H13.7224C18.8314 10.8577 22.9855 15.0118 22.9855 20.1208V20.64ZM1.10675 19.6015H21.9296C21.6612 15.308 18.0822 11.8962 13.7224 11.8962H9.3139C4.95417 11.8962 1.37509 15.308 1.10675 19.6015Z" fill="black"/>
                <path d="M11.5196 11.8963C8.2542 11.8963 5.59863 9.24071 5.59863 5.97527C5.59863 2.70983 8.2542 0.0507812 11.5196 0.0507812C14.7851 0.0507812 17.4406 2.70635 17.4406 5.97179C17.4406 9.23723 14.7851 11.8928 11.5196 11.8928V11.8963ZM11.5196 1.09279C8.82574 1.09279 6.63715 3.28137 6.63715 5.97527C6.63715 8.66918 8.82922 10.8578 11.5196 10.8578C14.2101 10.8578 16.4021 8.66918 16.4021 5.97527C16.4021 3.28137 14.2135 1.09279 11.5196 1.09279Z" fill="black"/>
                <path d="M22.9855 20.64H0.0507812V20.1208C0.0507812 15.0118 4.2049 10.8577 9.3139 10.8577H13.7224C18.8314 10.8577 22.9855 15.0118 22.9855 20.1208V20.64ZM1.10675 19.6015H21.9296C21.6612 15.308 18.0822 11.8962 13.7224 11.8962H9.3139C4.95417 11.8962 1.37509 15.308 1.10675 19.6015Z" stroke="black" strokeWidth="0.1"/>
                <path d="M11.5196 11.8963C8.2542 11.8963 5.59863 9.24071 5.59863 5.97527C5.59863 2.70983 8.2542 0.0507812 11.5196 0.0507812C14.7851 0.0507812 17.4406 2.70635 17.4406 5.97179C17.4406 9.23723 14.7851 11.8928 11.5196 11.8928V11.8963ZM11.5196 1.09279C8.82574 1.09279 6.63715 3.28137 6.63715 5.97527C6.63715 8.66918 8.82922 10.8578 11.5196 10.8578C14.2101 10.8578 16.4021 8.66918 16.4021 5.97527C16.4021 3.28137 14.2135 1.09279 11.5196 1.09279Z" stroke="black" strokeWidth="0.1"/>
                </svg>
              ) : (
                <svg xmlns="http://www.w3.org/2000/svg" width="23" height="21" viewBox="0 0 23 21" fill="none">
                <path d="M22.9348 20.5893H0V20.07C0 14.961 4.15412 10.8069 9.26312 10.8069H13.6716C18.7807 10.8069 22.9348 14.961 22.9348 20.07V20.5893ZM1.05596 19.5507H21.8788C21.6105 15.2572 18.0314 11.8454 13.6716 11.8454H9.26312C4.90339 11.8454 1.32431 15.2572 1.05596 19.5507Z" fill="#77808A"/>
                <path d="M11.4689 11.8455C8.20341 11.8455 5.54785 9.18993 5.54785 5.92449C5.54785 2.65905 8.20341 0 11.4689 0C14.7343 0 17.3899 2.65557 17.3899 5.92101C17.3899 9.18645 14.7343 11.842 11.4689 11.842V11.8455ZM11.4689 1.04201C8.77495 1.04201 6.58637 3.23059 6.58637 5.92449C6.58637 8.61839 8.77844 10.807 11.4689 10.807C14.1593 10.807 16.3513 8.61839 16.3513 5.92449C16.3513 3.23059 14.1628 1.04201 11.4689 1.04201Z" fill="#77808A"/>
                </svg>
              )}
              </div>
              <span className={activeButton === "profile" ? classes.activeText : classes.nonActiveText}>
                Profil
              </span>
            </div>
          </Link>
          </div>
        )}
      </header>
      )}
    </>
  );
};

export default NavBarPhone;